import { Alert, AlertDescription } from "@/components/ui/alert";
import { Bell, Calendar, Star } from "lucide-react";
import { useTranslation } from "react-i18next";
import { formatDate } from "@/utils/dateUtils";
import { isSubscriptionExpired } from "@/components/subscriptions/SubscriptionFormSchema";
import { isSubscriptionExpiringSoon } from "../ClientUtils";
import { Client } from "@/types/types";

interface ClientSubscriptionInfoProps {
  client: Client;
}

export function ClientSubscriptionInfo({ client }: ClientSubscriptionInfoProps) {
  const { t } = useTranslation("clients");
  
  const subscriptionEndDate = client.expiryDate;
  const isExpired = isSubscriptionExpired(subscriptionEndDate);

  return (
    <div className="pt-4 border-t">
      <h4 className="text-sm font-semibold mb-2">{t("currentSubscription")}</h4>
      <div className="space-y-1 mb-2">
        <p className="text-sm font-medium">{t(`${client.membershipType}`, { ns: "subscriptions" })}</p>
        <p className="text-sm text-muted-foreground">
          {isExpired
            ? t("expiredSince", { date: formatDate(subscriptionEndDate) })
            : t("expiresOn", { date: formatDate(subscriptionEndDate) })}
        </p>
      </div>

      <div className="grid grid-cols-2 gap-2 mb-4">
        <div className="flex items-center bg-amber-50 dark:bg-amber-950 p-2 rounded">
          <Calendar className="h-4 w-4 mr-2 text-amber-500" />
          <div className="text-sm">
            <p className="font-semibold">{t('sessions.remaining')}</p>
            {/* `a + "/" + b || 0` : la concaténation l'emportait sur le `||`,
                d'où un littéral « undefined/undefined ». Le repli doit porter
                sur chaque terme. */}
            <p className="text-lg font-bold">
              {`${client.sessionsRemaining ?? 0}/${client.sessionsTotal ?? 0}`}
            </p>
          </div>
        </div>
        <div className="flex items-center bg-purple-50 dark:bg-purple-950 p-2 rounded">
          <Star className="h-4 w-4 mr-2 text-purple-500" />
          <div className="text-sm">
            <p className="font-semibold">{t('jokers.remaining')}</p>
            <p className="text-lg font-bold">
              {`${client.jokersRemaining ?? 0}/${client.jokersTotal ?? 0}`}
            </p>
          </div>
        </div>
      </div>

      {isSubscriptionExpiringSoon(client) && (
        <Alert className="bg-amber-50 text-amber-800 border-amber-200 mb-4">
          <AlertDescription className="flex items-center text-sm">
            <Bell className="h-4 w-4 mr-2" />
            {t("subscriptionExpiringMessage")}
          </AlertDescription>
        </Alert>
      )}

      {client.status === "Blocked" && (
        <Alert className="bg-red-50 text-red-800 border-red-200 mb-4">
          <AlertDescription className="flex items-center text-sm">
            <Bell className="h-4 w-4 mr-2" />
            {t("accountBlockedForPayment")}
          </AlertDescription>
        </Alert>
      )}
    </div>
  );
}
